import { useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTranslation } from 'react-i18next';
import { Image } from "lucide-react";
import { userAPI } from "../UserAPI";

import { ResourceDisclosureHeader, ResourceDisclosurePanel } from './ResourceDisclosure';

// Global state for expanded resources to persist across re-renders
const expandedResources = new Map();

/**
 * @typedef {Object} ImageResourceProps
 * @property {any} resource
 * @property {string} [stableKey]
 */

/**
 * @param {ImageResourceProps} props
 */
export const ImageResource = ({ resource, stableKey = null }) => {
    // Always call hooks first
    const { t } = useTranslation();
    const resourceKey = stableKey || resource?.reference || resource?._id;
    const [expanded, setExpanded] = useState(() => expandedResources.get(resourceKey) || false);

    // Defensive programming - ensure resource is valid
    if (!resource) {
        return <div>No resource provided</div>;
    }

    // Ensure resource is an object
    if (typeof resource !== 'object') {
        return <div>Invalid resource format</div>;
    }

    // TODO Hack until we get both from the image processing
    resource.title = resource.title || resource.details;

    const hasDetails = typeof resource.details === 'string' && resource.details.trim().length > 0;

    // Apply color scheme

    return <div className="box-border w-full">
        <ResourceDisclosureHeader
            expanded={expanded}
            onToggle={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const newExpanded = !expanded;
                setExpanded(newExpanded);
                expandedResources.set(resourceKey, newExpanded);
            }}
            testId="chat-resource-image-toggle"
            icon={<Image aria-hidden="true" className="h-5 w-5 shrink-0" />}
            title={resource.title}
        />
        <ResourceDisclosurePanel expanded={expanded}>
            <div>
                <div className="flex justify-center">
                    {resource.url && (
                        <img src={userAPI.getResourceURL(resource.url)} alt={resource.title || ''} className="block h-auto w-full" />
                    )}
                </div>
                {/* Details are optional — a generated image carries a title and a URL but no
                    description. Only say something when there is nothing at all to show. */}
                {hasDetails && (
                    <div className="mt-4 text-sm">
                        <Markdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>{resource.details}</Markdown>
                    </div>
                )}
                {!hasDetails && !resource.url && (
                    <div className="mt-4 text-sm">{t('imageResource.nothingToShow')}</div>
                )}
            </div>
        </ResourceDisclosurePanel>
    </div>;
}
